import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Image as ImageIcon, ChevronDown, ChevronUp } from "lucide-react";

const empty = {
  name: "",
  description: "",
  sku: "",
  tagline: "",
  image_url: "",
  lifestyle_image_url: "",
  price_from: "",
  height_cm: "",
  firmness: "",
  buy_link: "",
  features: [
    { title: "", description: "" },
    { title: "", description: "" },
    { title: "", description: "" },
    { title: "", description: "" },
  ],
};

export default function ProductsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showShowcase, setShowShowcase] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/products");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditing(null); setShowForm(false); setShowShowcase(false); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price_from: form.price_from === "" ? null : Number(form.price_from),
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        features: form.features.filter((f) => f.title || f.description),
      };
      if (editing) {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Prodotto creato");
      }
      reset();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const edit = (p) => {
    const features = (p.features && p.features.length ? p.features : []).slice(0, 4);
    while (features.length < 4) features.push({ title: "", description: "" });
    setForm({
      name: p.name || "",
      description: p.description || "",
      sku: p.sku || "",
      tagline: p.tagline || "",
      image_url: p.image_url || "",
      lifestyle_image_url: p.lifestyle_image_url || "",
      price_from: p.price_from ?? "",
      height_cm: p.height_cm ?? "",
      firmness: p.firmness || "",
      buy_link: p.buy_link || "",
      features,
    });
    setEditing(p.id);
    setShowForm(true);
    setShowShowcase(Boolean(p.image_url || p.price_from));
  };

  const del = async (id) => {
    if (!confirm("Eliminare questo prodotto?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Prodotto eliminato");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateFeature = (idx, key, value) => {
    const next = form.features.slice();
    next[idx] = { ...next[idx], [key]: value };
    setForm({ ...form, features: next });
  };

  return (
    <div className="space-y-8" data-testid="products-manager">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="mgx-display text-4xl text-[#003D5C]">Catalogo prodotti</h2>
          <p className="text-sm text-[#6B7280] mt-2">Definisci i prodotti Magniflex e i loro dati di showcase (immagini, prezzo, feature, link d'acquisto).</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} className="mgx-btn-primary gap-2" data-testid="add-product-btn">
          <Plus size={14} /> Nuovo prodotto
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mgx-card space-y-6" data-testid="product-form">
          <div className="flex items-center justify-between">
            <div className="mgx-label">{editing ? "Modifica prodotto" : "Nuovo prodotto"}</div>
            <button type="button" onClick={reset} className="text-[#6B7280] hover:text-[#003D5C]"><X size={18} /></button>
          </div>

          {/* Basic info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="mgx-label">Nome prodotto *</label>
              <input required className="mgx-input mt-1" placeholder="Magnistretch 9" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
            </div>
            <div>
              <label className="mgx-label">SKU</label>
              <input className="mgx-input mt-1" placeholder="MPO225AA00536" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" />
            </div>
            <div className="md:col-span-2">
              <label className="mgx-label">Tagline (mostrata sotto al nome nella showcase)</label>
              <input className="mgx-input mt-1" placeholder="Il materasso che hai dormito in hotel" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="product-tagline-input" />
            </div>
            <div className="md:col-span-2">
              <label className="mgx-label">Descrizione breve</label>
              <textarea className="mgx-input mt-1 min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" />
            </div>
          </div>

          {/* Showcase toggle */}
          <button
            type="button"
            onClick={() => setShowShowcase(!showShowcase)}
            className="flex items-center gap-2 text-sm font-semibold text-[#003D5C] hover:text-[#E30613] transition-colors"
            data-testid="toggle-showcase-btn"
          >
            {showShowcase ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Dati showcase per la landing (immagini, prezzo, feature, link d'acquisto)
          </button>

          {showShowcase && (
            <div className="space-y-6 border-l-2 border-[#E30613] pl-6 ml-2">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="mgx-label flex items-center gap-2"><ImageIcon size={11} /> URL immagine prodotto (hero)</label>
                  <input type="url" className="mgx-input mt-1" placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="product-image-input" />
                  {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-24 object-contain border border-[#E5E7EB]" />}
                </div>
                <div>
                  <label className="mgx-label flex items-center gap-2"><ImageIcon size={11} /> URL immagine lifestyle</label>
                  <input type="url" className="mgx-input mt-1" placeholder="https://..." value={form.lifestyle_image_url} onChange={(e) => setForm({ ...form, lifestyle_image_url: e.target.value })} data-testid="product-lifestyle-input" />
                  {form.lifestyle_image_url && <img src={form.lifestyle_image_url} alt="" className="mt-2 h-24 object-cover border border-[#E5E7EB]" />}
                </div>
                <div>
                  <label className="mgx-label">Prezzo (€)</label>
                  <input type="number" step="0.01" className="mgx-input mt-1" placeholder="1189" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} data-testid="product-price-input" />
                </div>
                <div>
                  <label className="mgx-label">Altezza (cm)</label>
                  <input type="number" step="0.5" className="mgx-input mt-1" placeholder="23" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} data-testid="product-height-input" />
                </div>
                <div>
                  <label className="mgx-label">Rigidità</label>
                  <input className="mgx-input mt-1" placeholder="Medium-Firm" value={form.firmness} onChange={(e) => setForm({ ...form, firmness: e.target.value })} data-testid="product-firmness-input" />
                </div>
                <div>
                  <label className="mgx-label">Link d'acquisto</label>
                  <input type="url" className="mgx-input mt-1" placeholder="https://www.magniflex.com/..." value={form.buy_link} onChange={(e) => setForm({ ...form, buy_link: e.target.value })} data-testid="product-buy-link-input" />
                </div>
              </div>

              <div>
                <label className="mgx-label">Feature di prodotto (fino a 4)</label>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  {form.features.map((f, i) => (
                    <div key={i} className="border border-[#E5E7EB] p-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Feature {i + 1}</div>
                      <input className="mgx-input" placeholder="Titolo (es. Distende la colonna)" value={f.title} onChange={(e) => updateFeature(i, "title", e.target.value)} data-testid={`product-feature-title-${i}`} />
                      <textarea className="mgx-input min-h-[60px]" placeholder="Descrizione..." value={f.description} onChange={(e) => updateFeature(i, "description", e.target.value)} data-testid={`product-feature-desc-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2 border-t border-[#E5E7EB]">
            <button type="submit" disabled={loading} className="mgx-btn-primary" data-testid="product-save-btn">
              {editing ? "Salva modifiche" : "Crea prodotto"}
            </button>
            <button type="button" onClick={reset} className="mgx-btn-secondary">Annulla</button>
          </div>
        </form>
      )}

      <div className="mgx-card">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.15em] text-[#6B7280]">
              <th className="py-3 pr-4">Prodotto</th>
              <th className="py-3 pr-4">SKU</th>
              <th className="py-3 pr-4 text-right">Prezzo</th>
              <th className="py-3 pr-4">Showcase</th>
              <th className="py-3 pr-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-[#6B7280]">Nessun prodotto. Creane uno.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[#E5E7EB]/60">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-10 h-10 object-cover border border-[#E5E7EB]" />
                    ) : (
                      <div className="w-10 h-10 bg-[#F5F6F7] border border-[#E5E7EB] flex items-center justify-center text-[#6B7280]"><ImageIcon size={14} /></div>
                    )}
                    <div>
                      <div className="mgx-display text-base text-[#003D5C]">{p.name}</div>
                      {p.tagline && <div className="text-xs text-[#6B7280] mt-0.5">{p.tagline}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 text-[#6B7280] font-mono text-xs">{p.sku || "—"}</td>
                <td className="py-3 pr-4 text-right">{p.price_from ? `€ ${p.price_from}` : "—"}</td>
                <td className="py-3 pr-4">
                  {p.image_url && p.buy_link ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-[#003D5C] font-semibold">✓ Completo</span>
                  ) : (
                    <span className="text-[11px] uppercase tracking-[0.15em] text-[#6B7280]">Parziale</span>
                  )}
                </td>
                <td className="py-3 pr-4 text-right">
                  <button onClick={() => edit(p)} className="text-[#003D5C] hover:text-[#E30613] mr-3" data-testid={`product-edit-${p.id}`}>
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => del(p.id)} className="text-[#E30613] hover:opacity-70" data-testid={`product-delete-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
